import type { ApiResponse } from '@shared/contracts/common';

/**
 * Tek HTTP istemcisi.
 *
 * Prototipte her handler kendi `fetch` çağrısını yapıyordu ve en tehlikeli
 * satır şuydu (App.tsx:213):
 *     catch (err) {
 *       showToast('Sunucu bağlantısı kurulamadı. İşlem yerel modda işlendi.', 'success');
 *     }
 * Ağ hatası kullanıcıya BAŞARI olarak gösteriliyordu: rezervasyon hiç
 * oluşmamışken üye derse geleceğini sanıyordu. Sessiz hata yutma, üretimde
 * en pahalı hata sınıfıdır.
 *
 * Burada her hata `ApiError` olarak fırlatılır; TanStack Query yakalar,
 * `onError` kullanıcıya gerçeği söyler.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Yeniden denemenin anlamı var mı? 4xx'te yok. */
  get isRetryable(): boolean {
    return this.status >= 500 || this.status === 0;
  }
}

const BASE_URL = '/api/v1';

/**
 * Oturum yenilemenin denenMEyeceği uçlar.
 *
 * Bunlarda 401 "token süresi doldu" değil "kimlik bilgileri yanlış" demektir;
 * yenilemeye çalışmak hem anlamsız hem de `/auth/refresh` için sonsuz döngüdür.
 */
const NO_REFRESH_PATHS = ['/auth/refresh', '/auth/login', '/auth/register'];

/**
 * Tek uçuşlu (single-flight) oturum yenileme.
 *
 * ## Neden tek uçuşlu olmak ZORUNDA
 *
 * Sunucu tarafında yenileme artık gerçek bir rotasyondur: her `/auth/refresh`
 * çağrısı `tokenVersion`'ı artırır ve eski refresh token'ı öldürür (çalınan bir
 * token'ın ömrünü kısaltmanın tek yolu budur). Bunun doğrudan sonucu şudur:
 * AYNI eski token ile yapılan ikinci bir yenileme isteği başarısız olur.
 *
 * Bir sayfa açılışında dört sorgu (me, sessions, slots, bookings) paralel gider.
 * Access token süresi dolmuşsa dördü de 401 alır. Kilit olmasaydı dördü birden
 * yenileme isteği gönderir, biri kazanır, diğer üçü "token yeniden kullanıldı"
 * hatası alır ve kullanıcı — hiçbir şey yapmamışken — oturumdan düşerdi.
 *
 * Kilit sayesinde ilk 401 yenilemeyi başlatır, diğerleri AYNI promise'i bekler.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  refreshInFlight ??= fetch(`${BASE_URL}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
  })
    .then((response) => response.ok)
    .catch(() => false)
    // Promise SETTLE olduktan sonra temizlenir; erken temizlemek ikinci bir
    // yenileme turunu tetikleyip kilidin amacını ortadan kaldırırdı.
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /**
   * Ham gövde — JSON'a çevrilmeden gönderilir. Yalnızca dosya yüklemede.
   *
   * Doğrudan `fetch` çağırmak yerine bu seçeneğin eklenmesinin sebebi
   * `request()` içindeki TEK UÇUŞLU TOKEN YENİLEME. Yükleme onu atlasaydı,
   * access token'ın süresi dolmuşken başlatılan bir yükleme 401 alır ve
   * yenilenmeden düşerdi — üstelik bu, yalnızca 15 dakikadan uzun süre açık
   * kalmış bir sekmede görülen türden bir hata olurdu.
   */
  rawBody?: BodyInit;
  /** Yazma işlemlerinde çift gönderimi engeller. */
  idempotencyKey?: string;
}

function buildInit(options: RequestOptions): RequestInit {
  const { body, rawBody, idempotencyKey, headers, ...rest } = options;
  const hasJsonBody = body !== undefined && rawBody === undefined;

  return {
    ...rest,
    credentials: 'include', // httpOnly cookie'ler için zorunlu
    headers: {
      ...(hasJsonBody ? { 'Content-Type': 'application/json' } : {}),
      ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
      ...headers,
    },
    ...(rawBody !== undefined ? { body: rawBody } : {}),
    ...(hasJsonBody ? { body: JSON.stringify(body) } : {}),
  };
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const init = buildInit(options);

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, init);

    /* 401 → bir kez oturum yenilenip istek TEKRARLANIR.
       Yeniden gönderim güvenlidir: istek yetkilendirme katmanında durdu, hiçbir
       iş mantığı çalışmadı, dolayısıyla yan etkisi yok. Idempotency-Key de
       korunduğu için sunucu tarafında çift işlem riski ayrıca kapalı. */
    if (
      response.status === 401 &&
      !NO_REFRESH_PATHS.some((candidate) => path.startsWith(candidate)) &&
      (await refreshSession())
    ) {
      response = await fetch(`${BASE_URL}${path}`, buildInit(options));
    }
  } catch (cause) {
    // Ağ katmanı hatası. ASLA başarı olarak sunulmaz.
    throw new ApiError(
      0,
      'NETWORK_ERROR',
      'Sunucuya ulaşılamıyor. İnternet bağlantınızı kontrol edip tekrar deneyin.',
      cause,
    );
  }

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    if (!response.ok) {
      throw new ApiError(response.status, 'UNEXPECTED_RESPONSE', 'Sunucudan beklenmeyen bir yanıt alındı.');
    }
    return (await response.text()) as T;
  }

  const payload = (await response.json()) as ApiResponse<T>;

  if (!response.ok || payload.success === false) {
    const error = 'error' in payload ? payload.error : undefined;
    throw new ApiError(
      response.status,
      error?.code ?? 'UNKNOWN_ERROR',
      error?.message ?? 'Beklenmeyen bir hata oluştu.',
      error?.details,
      'requestId' in payload ? payload.requestId : undefined,
    );
  }

  return payload.data;
}

export const api = {
  get: <T>(path: string, init?: RequestOptions) => request<T>(path, { ...init, method: 'GET' }),
  post: <T>(path: string, body?: unknown, init?: RequestOptions) =>
    request<T>(path, { ...init, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, init?: RequestOptions) =>
    request<T>(path, { ...init, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, init?: RequestOptions) =>
    request<T>(path, { ...init, method: 'PUT', body }),
  delete: <T>(path: string, init?: RequestOptions) => request<T>(path, { ...init, method: 'DELETE' }),
};

/** Kullanıcıya gösterilecek hata metni — teknik ayrıntı sızdırmaz. */
export function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Beklenmeyen bir hata oluştu.';
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/** Sorgu string'i üretir; undefined değerleri atar. */
export function qs(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const result = search.toString();
  return result ? `?${result}` : '';
}
